import React, { useState, useEffect } from "react";
import "./patientInsurance.css";
import { useTranslation } from "react-i18next";
import Widgets from "../../widgets";
import { useDispatch, useSelector } from "react-redux";
import ToastPopup from "../../widgets/Toast/toastPopup";
import { uploadMedia } from "../../modules/patientProfile/patientProfileThunk";
import { resetRejectHnReq, resetUploadMedia } from "../../modules/patientProfile/patientProfileSlice";
import useValidation from "../../utils/validators";
import moment from "moment";

const PatientInsurance = ({ userId, setSelectedView, setPage, userInsurance, setRequestData, caterogy, hasHn }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [action, setAction] = useState(null);
  const [showReject, setShowReject] = useState(false);
  const { validateInsurance } = useValidation();
  const { uploadMediaSuccess, uploadMediaErr, rejectHnReqErr, rejectHnReq } = useSelector((state) => state.patientProfile);

  useEffect(() => {
    if (rejectHnReqErr) {
      ToastPopup(rejectHnReqErr);
    }
    if (rejectHnReq && rejectHnReq.data.rejected) {
      ToastPopup.success(t("reqRejected"));
      setSelectedView("patientProfileList");
    }
    dispatch(resetRejectHnReq());
  }, [rejectHnReqErr, rejectHnReq]);

  const handleImageUpload =
    (index, idType) =>
    ({ files }) => {
      dispatch(uploadMedia({ files }));
      setAction({ index, idType });
    };

  useEffect(() => {
    if (uploadMediaErr) {
      ToastPopup.error(uploadMediaErr);
    }
    if (uploadMediaSuccess && uploadMediaSuccess.status === "success") {
      const url = uploadMediaSuccess.data?.urls?.[0];

      if (url) {
        const finalUrl = url.replace(/ /g, "%20");
        const { index, idType } = action;
        setRequestData((prevData) => {
          const updatedInsuranceList = [...prevData.insurance];
          if (idType === "frontImg") {
            updatedInsuranceList[index].insurance.image = finalUrl;
          }
          return {
            ...prevData,
            insurance: updatedInsuranceList,
          };
        });
      }
    }

    dispatch(resetUploadMedia());
  }, [uploadMediaErr, uploadMediaSuccess, action, dispatch]);

  const handleInputChange = (index, field, value) => {
    const filteredValue = value
      .trimStart()
      .slice(0, 50);
    setRequestData((prevData) => {
      const updatedInsuranceList = [...prevData.insurance];
      updatedInsuranceList[index].insurance[field] = filteredValue;
      return {
        ...prevData,
        insurance: updatedInsuranceList,
      };
    });
  };

  const handleAddInsurance = () => {
    setRequestData((prevData) => {
      const existingInsurance = prevData.insurance || [];

      if (existingInsurance.length < 3) {
        const newInsurance = {
          insurance: {
            company: "",
            image: "",
          },
          isVerified: true,
        };
        return {
          ...prevData,
          insurance: [...existingInsurance, newInsurance],
        };
      }

      return prevData;
    });
  };

  const handleRemoveInsurance = (index) => {
    setRequestData((prevData) => {
      const existingInsurance = prevData.insurance || [];
      if (existingInsurance.length > 0 && !existingInsurance[index]._id) {
        const updatedInsuranceList = [...existingInsurance];
        updatedInsuranceList.splice(index, 1);
        return {
          ...prevData,
          insurance: updatedInsuranceList,
        };
      }
      return prevData;
    });
  };

  const isAddButtonDisabled = () => {
    if (!userInsurance || userInsurance.length === 0) {
      return false;
    }
    const currentInsurance = userInsurance[userInsurance.length - 1].insurance;
    return !currentInsurance.company || !currentInsurance.image;
  };

  const setImage = (index, idType, image) => {
    setRequestData((prevData) => {
      const updatedInsuranceList = prevData.insurance.map((insuranceItem, i) => {
        if (i === index) {
          return {
            ...insuranceItem,
            insurance: {
              ...insuranceItem.insurance,
              image: idType === "frontImg" ? image : insuranceItem.insurance.image,
            },
          };
        }
        return insuranceItem;
      });
      return {
        ...prevData,
        insurance: updatedInsuranceList,
      };
    });
  };

  const handleVerify = () => {
    const isValid = validateInsurance(userInsurance, caterogy);
    if (isValid) {
      setPage(5);
    }
  };
  const handleReject = () => {
    setShowReject(true);
  };

  const handleCancelReject = () => {
    setShowReject(false);
  };

  const handleConfirmReject = () => {
    setShowReject(false);
  };

  const handleCheckboxChange = (index) => {
    setRequestData((prevData) => {
      const updatedInsuranceList = prevData.insurance.map((insurance, i) => {
        if (i === index) {
          return {
            ...insurance,
            isVerified: !insurance.isVerified,
          };
        }
        return insurance;
      });
      return {
        ...prevData,
        insurance: updatedInsuranceList,
      };
    });
  };

  const insuranceCategoryMapping = {
    [`${t("opdInterInsured")}`]: "OPD International Insured",
    [`${t("opdLocalInsured")}`]: "OPD Local Insured",
    [`${t("opdSubsidized")}`]: "OPD Subsidized",
  };

  const handleInsuranceCategoryChange = (selectedCategory) => {
    const englishCategory = insuranceCategoryMapping[selectedCategory];

    setRequestData((prevData) => {
      return {
        ...prevData,
        insuranceCategory: englishCategory,
        insuranceCategoryToShow: selectedCategory,
      };
    });
  };

  const renderInsuranceFields = () => {
    return userInsurance.map((insurance, index) => (
      <div key={index} className="piv-id nat-id">
        <p className="piv-subtitle with-checkbox">
          <input
            type="checkbox"
            checked={insurance?.isVerified}
            onChange={() => handleCheckboxChange(index)}
            className={`piv-checkbox insurance-checkbox ${insurance.isVerifiedFS ? "disabled" : ""}`}
            disabled={!insurance?._id}
          />
          {t("insuranceCmpName")}
          <span className="asterik">*</span>
        </p>
        <div className="input-insurance">
          <input
            className="piv-number"
            value={insurance?.insurance?.company}
            disabled={insurance?._id}
            placeholder={t("enterInsuranceName")}
            onChange={(e) => handleInputChange(index, "company", e.target.value)}
          />
        </div>
        <div className="id-select-card">
          <Widgets.SelectCard
            image={insurance?.insurance?.image}
            title="insuranceImg"
            idType={`frontImg-${index}`}
            front
            setImage={(image) => setImage(index, "frontImg", image)}
            onImageUpload={handleImageUpload(index, "frontImg")}
            disabled={!insurance?.insurance?.company || insurance?._id}
          />
        </div>
        {insurance?.updatedAt || insurance?.createdAt ? (
          <div className="iv-updated-time">
            <span>
              <span style={{ display: "inline-block", marginTop: 8, color: "#6B7280", fontSize: 12 }}>
                {t("updatedOn", "Updated on")}:
                {" "}
                {moment(insurance?.updatedAt || insurance?.createdAt).format("DD/MM/YYYY HH:mm")}
              </span>
            </span>
          </div>
        ) : null}
        <div className="save-speciality info">
          {!insurance?._id && (
            <>
              <div className="speciality-cancel reject" onClick={() => handleRemoveInsurance(index)}>
                {t("deletee")}
              </div>
            </>
          )}
          {userInsurance.length > 0 && userInsurance.length < 3 && index === userInsurance.length - 1 && (
            <div className={`speciality-save show-last ${isAddButtonDisabled() ? "disabled" : ""}`} onClick={(e) => (isAddButtonDisabled() ? "" : handleAddInsurance())}>
              {t("add")}
            </div>
          )}
        </div>
      </div>
    ));
  };

  return (
    <div className="pdl-info verify pdl-insurance">
      <div className="pav-add-header">
        <section>
          <p>{t("insuranceInfo")}</p>
        </section>
      </div>
      <div className="iv-insurance-list">
        {renderInsuranceFields()}
        {userInsurance.length === 0 && (
          <>
            <div className="save-speciality info">
              <div className={`speciality-save show-last}`} onClick={(e) => handleAddInsurance()}>
                {t("addInsurance")}
              </div>
            </div>
          </>
        )}
        <div className="pdl-hn">
          <p className="pdl-hn-title">
            {t("insuranceIndicator")}
            <span className="asterik">*</span>
          </p>
          <Widgets.SelectDropdown
            options={[`${t("opdInterInsured")}`, `${t("opdLocalInsured")}`, `${t("opdSubsidized")}`]}
            selectedValue={caterogy}
            onChange={handleInsuranceCategoryChange}
            placeholder="selCategory"
            big
          />
        </div>
      </div>
      <div className="save-speciality info">
        <>
          {hasHn && (
            <div className="speciality-cancel reject" onClick={handleReject}>
              {t("reject")}
            </div>
          )}
          <div className="speciality-save" onClick={() => handleVerify()}>
            {t("next")}
          </div>
        </>
      </div>
      <Widgets.Reject showModal={showReject} handleCancel={handleCancelReject} handleConfirm={handleConfirmReject} reason="rejInsuranceInfo" userId={userId} />
    </div>
  );
};

export default PatientInsurance;